import type { StepConfiguration, UpholsteryCar } from "@views/Upholstery/types"
import {
  getInitialStepValue,
  getPerforationOptions,
  getDefaultValue,
} from "@views/Upholstery/helpers/helper"
import {
  useUpholsteryDataStore,
  useUpholsteryNavigationStore,
  useUpholsteryUIStore,
  useUpholsteryOptionsStore,
  useUpholsterySelectionsStore,
  usePdmDataStore,
} from "@views/Upholstery/store/store"

export function applyUpholsteryVehicleToStores(
  vehicleData: UpholsteryCar,
  datoVehicle: UpholsteryCar
): void {
  const { actions: dataActions } = useUpholsteryDataStore.getState()
  const { actions: navigationActions } = useUpholsteryNavigationStore.getState()
  const { actions: uiActions } = useUpholsteryUIStore.getState()
  const { actions: optionsActions } = useUpholsteryOptionsStore.getState()
  const { actions: selectionsActions } = useUpholsterySelectionsStore.getState()

  const stepsWithPdm = vehicleData.steps

  dataActions.setSelectedVehicle(vehicleData)

  uiActions.setShowForm(false)
  uiActions.setIsFactoryBuild(false)

  selectionsActions.resetSelections()
  selectionsActions.resetRowsSelections()
  selectionsActions.resetBodySelections()
  selectionsActions.resetMaterialSelections()

  optionsActions.setOptionsFromVehicle({
    ...datoVehicle,
    steps: stepsWithPdm,
  })

  if (stepsWithPdm.length > 0) {
    const firstStep = stepsWithPdm[0]
    navigationActions.setIsSummaryActive(false)
    navigationActions.setRowCountChoiceConfirmed(false)
    navigationActions.setCurrentStepIndex(0)
    navigationActions.setCurrentOption(firstStep.key)
    navigationActions.setActive(firstStep.key)

    if (firstStep.substeps && firstStep.substeps.length > 0) {
      navigationActions.setCurrentSubOption(firstStep.substeps[0].key)
    } else {
      navigationActions.setCurrentSubOption(null)
    }

    const initialConfig = stepsWithPdm.reduce((config, step) => {
      const initialOptionValue = step.category?.optionValues[0]

      if (step.substeps.length > 0) {
        config[step.key] = {
          selectedValue: getInitialStepValue(step),
          sku: initialOptionValue?.sku || "",
          label: initialOptionValue?.optionName || "",
          image: initialOptionValue?.optionImage?.responsiveImage?.src,
          substeps: step.substeps.reduce((subs, substep) => {
            const shouldUseDefaultValue =
              step.key === "design" &&
              (substep.key === "perforation" ||
                substep.key === "color-insert-perforation") &&
              substep.defaultValue

            let initialSubOptionValue
            if (shouldUseDefaultValue) {
              const substepPerforationOptions = getPerforationOptions(
                substep.key,
                step.substeps
              )
              initialSubOptionValue = getDefaultValue(
                substep,
                step.key,
                substep.key,
                substepPerforationOptions
              )
            } else {
              initialSubOptionValue = substep.category?.optionValues[0]
            }

            subs[substep.key] = {
              value: initialSubOptionValue?.optionValue || "",
              sku: initialSubOptionValue?.sku || "",
              label: initialSubOptionValue?.optionName || "",
              image: initialSubOptionValue?.optionImage?.responsiveImage?.src,
              slug: initialSubOptionValue?.slug || "",
            }
            return subs
          }, {}),
        }
      } else {
        config[step.key] = {
          selectedValue: getInitialStepValue(step),
          sku: initialOptionValue?.sku || "",
          label: initialOptionValue?.optionName || "",
          image: initialOptionValue?.optionImage?.responsiveImage?.src,
        }
      }

      return config
    }, {} as StepConfiguration)

    dataActions.setActiveConfigurations(initialConfig)

    const initialBuild =
      initialConfig.build?.selectedValue === "factory" ? "factory" : "custom"
    selectionsActions.setSelectedBuild(initialBuild)
    uiActions.setIsFactoryBuild(initialBuild === "factory")
  }

  uiActions.setShowYourPicksModal(false)
}

/**
 * Wipe the live configurator choices after a kit is saved to the cart.
 * The cart line already holds its own snapshot, so this does not change the cart.
 * Catalog cars and page price flags stay as they are.
 */
export function resetUpholsteryConfigurator(): void {
  useUpholsteryDataStore.setState({
    selectedVehicle: null,
    activeConfigurations: {},
    rowCount: 0,
    rowType: "2-row",
    m3Number: "",
    factoryMatchSku: "",
    customBuildSku: "",
  })
  useUpholsterySelectionsStore.getState().actions.resetSelections()
  useUpholsteryNavigationStore.getState().actions.resetNavigation()
  const ui = useUpholsteryUIStore.getState().actions
  ui.setShowForm(false)
  ui.setIsFactoryBuild(false)
  usePdmDataStore.getState().actions.resetPdmData()
}
